/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { KeyboardAvoidingView} from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { ModalFormScroll } from '../../shared/modals/ModalFormScroll'
import { ModalHeroHeader } from '../../shared/modals/ModalHeroHeader'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles'
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors'
import { typography } from '../../shared/theme/typography';
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles';
import { listSuppliersLocalPaged } from '../../catalog/infra-api';
import { formatMoney } from '../../shared/format/public-api';
import {
  getSupplierAccountSummary,
  listSuppliersWithPurchases,
  type SupplierAccountSummary,
  type SupplierAccountEntry,
} from '../infra/purchasesRepoSupplierAccount';

type Props = {
  visible: boolean;
  supplierId: string | null;
  supplierName: string | null;
  branchId: string;
  /** Permiso Expenses:pay — muestra "Pagar saldo" cuando hay deuda. */
  canPay?: boolean;
  /** Abre el flujo de pago sobre el gasto pendiente más viejo del proveedor. */
  onPayBalance?: (supplierId: string) => void;
  onClose: () => void;
};

type PickerSupplier = { id: string; name: string };


function entryTypeLabel(type: SupplierAccountEntry['type']): string {
  switch (type) {
    case 'order':
      return 'Orden';
    case 'reception':
      return 'Recepción';
    case 'return':
      return 'Devolución';
    case 'expense':
      return 'Gasto';
  }
}

export function SupplierAccountModal({ visible, supplierId, supplierName, branchId, canPay = false, onPayBalance, onClose }: Props) {
  const { width, height: winH } = useWindowDimensions();
  const isMobile = width < CONTENT_WIDE_BREAKPOINT;
  const backdropPadding = useModalBackdropPadding();
  const insets = useSafeAreaInsets();

  const [pickerSuppliers, setPickerSuppliers] = useState<PickerSupplier[]>([]);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [selected, setSelected] = useState<PickerSupplier | null>(null);
  const [summary, setSummary] = useState<SupplierAccountSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);

  useEffect(() => {
    if (!visible) {
      setPickerSuppliers([]);
      setPickerLoaded(false);
      setSelected(null);
      setSummary(null);
      setLoadingSummary(false);
      setPendingOnly(false);
      return;
    }
    setSelected(supplierId ? { id: supplierId, name: supplierName ?? 'Proveedor' } : null);
    setSummary(null);
    setPickerLoaded(false);
    let cancelled = false;
    Promise.all([
      listSuppliersLocalPaged({ limit: 500, offset: 0 }),
      branchId ? listSuppliersWithPurchases(branchId) : Promise.resolve([]),
    ])
      .then(([catalog, withPurchases]) => {
        if (cancelled) return;
        const byId = new Map<string, PickerSupplier>();
        for (const row of catalog) {
          byId.set(row.id, { id: row.id, name: row.name });
        }
        for (const row of withPurchases) {
          if (!byId.has(row.supplierId)) {
            byId.set(row.supplierId, { id: row.supplierId, name: row.supplierName });
          }
        }
        setPickerSuppliers(
          [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'es')),
        );
      })
      .catch((err) => {
        console.warn('[SupplierAccountModal] picker', err);
        if (!cancelled) setPickerSuppliers([]);
      })
      .finally(() => {
        if (!cancelled) setPickerLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, supplierId, supplierName, branchId]);

  useEffect(() => {
    if (!visible || !selected) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    setLoadingSummary(true);
    getSupplierAccountSummary(branchId, selected.id, selected.name)
      .then((next) => {
        if (!cancelled) setSummary(next);
      })
      .catch((err) => {
        console.warn('[SupplierAccountModal]', err);
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingSummary(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, selected, branchId]);

  // "Solo pendientes": gastos sin pagar o pagados parcialmente.
  const visibleEntries =
    summary == null
      ? []
      : pendingOnly
        ? summary.entries.filter(
            (e) => e.type === 'expense' && (e.status === 'unpaid' || e.status === 'partial'),
          )
        : summary.entries;

  const content = selected ? (
    <>
      <ModalHeroHeader
        kicker="Cuenta corriente"
        title={selected.name}
        subtitle={summary ? `${summary.entries.length} movimientos` : ''}
        badge={
          summary ? (
            <StatusPill
              label={summary.balance > 0 ? `Saldo: ${formatMoney(summary.balance)}` : 'Sin saldo'}
              tone={summary.balance > 0 ? 'warning' : 'success'}
              compact
            />
          ) : undefined
        }
      />
      <View style={styles.heroActionsRow}>
        <TouchableOpacity
          onPress={() => {
            setSelected(null);
            setSummary(null);
          }}
          accessibilityRole="button"
          accessibilityLabel="Cambiar proveedor"
          style={styles.changeSupplierBtn}
        >
          <Text style={styles.changeSupplierText}>Cambiar proveedor</Text>
        </TouchableOpacity>
        {canPay && onPayBalance && summary && summary.balance > 0 ? (
          <TouchableOpacity
            onPress={() => onPayBalance(selected.id)}
            accessibilityRole="button"
            accessibilityLabel={`Pagar saldo de ${formatMoney(summary.balance)}`}
            style={styles.payBalanceBtn}
            testID="supplier-account.pay-balance"
          >
            <Text style={styles.payBalanceBtnText}>Pagar saldo · {formatMoney(summary.balance)}</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {loadingSummary ? (
        <ModalStatePanel loading loadingMessage="Cargando cuenta corriente…" />
      ) : !summary || summary.entries.length === 0 ? (
        <ModalStatePanel empty emptyTitle="Sin movimientos" emptyMessage="Sin movimientos para este proveedor." />
      ) : (
        <>
          <View style={styles.metricsRow}>
            <View style={styles.metric}>
              <Text style={styles.metricLabel}>Total comprado</Text>
              <Text style={styles.metricValue}>{formatMoney(summary.totalOrdered)}</Text>
            </View>
            <View style={styles.metric}>
              <Text style={styles.metricLabel}>Total gastado</Text>
              <Text style={styles.metricValue}>{formatMoney(summary.totalExpenses)}</Text>
            </View>
            <View style={styles.metric}>
              <Text style={styles.metricLabel}>Pagado</Text>
              <Text style={styles.metricValue}>{formatMoney(summary.totalPaid)}</Text>
            </View>
            <View style={styles.metric}>
              <Text style={styles.metricLabel}>Saldo</Text>
              <Text style={[styles.metricValue, summary.balance > 0 && styles.metricWarn]}>
                {formatMoney(summary.balance)}
              </Text>
            </View>
          </View>
          {summary.returnCredit > 0 ? (
            <Text style={styles.creditHint} testID="supplier-account.return-credit">
              Tenés {formatMoney(summary.returnCredit)} de crédito por devoluciones o NC — ya lo descontamos del saldo.
            </Text>
          ) : null}

          <View style={styles.section}>
            <View style={styles.sectionHeaderRow}>
              <Text style={styles.sectionTitle}>
                Movimientos ({visibleEntries.length})
              </Text>
              <TouchableOpacity
                onPress={() => setPendingOnly((v) => !v)}
                accessibilityRole="button"
                accessibilityLabel={pendingOnly ? 'Mostrar todos los movimientos' : 'Mostrar solo pendientes'}
                style={[styles.pendingToggle, pendingOnly && styles.pendingToggleActive]}
                testID="supplier-account.pending-only"
              >
                <Text style={[styles.pendingToggleText, pendingOnly && styles.pendingToggleTextActive]}>
                  Solo pendientes
                </Text>
              </TouchableOpacity>
            </View>
            {visibleEntries.length === 0 ? (
              <ModalStatePanel empty emptyTitle="Sin pendientes" emptyMessage="No quedan gastos por pagar con este proveedor." />
            ) : (
              visibleEntries.map((entry, index) => (
                <View
                  key={`${entry.type}-${entry.id}`}
                  style={[styles.entryRow, index === visibleEntries.length - 1 && styles.entryRowLast]}
                >
                  <View style={styles.entryHeader}>
                    <Text style={modalExperience.listRowTitle}>{entryTypeLabel(entry.type)}</Text>
                    <Text style={styles.entryAmount}>{formatMoney(entry.amount)}</Text>
                  </View>
                  <Text style={modalExperience.listMeta}>{entry.date}</Text>
                  <Text style={modalExperience.listMetaMuted}>{entry.description}</Text>
                </View>
              ))
            )}
          </View>
        </>
      )}
    </>
  ) : !pickerLoaded ? (
    <ModalStatePanel loading loadingMessage="Cargando proveedores…" />
  ) : pickerSuppliers.length === 0 ? (
    <ModalStatePanel
      empty
      emptyTitle="No hay proveedores"
      emptyMessage="Cargá un proveedor en Catálogo para ver la cuenta"
    />
  ) : (
    <>
      <Text style={styles.pickerTitle}>Elegí un proveedor</Text>
      <Text style={styles.pickerHint}>Mostramos los del catálogo y los que ya tienen compras.</Text>
      <View style={styles.pickerRow}>
        {pickerSuppliers.map((s) => (
          <TouchableOpacity
            key={s.id}
            style={styles.chip}
            onPress={() => setSelected(s)}
            accessibilityRole="button"
            accessibilityLabel={s.name}
          >
            <Text style={styles.chipText}>{s.name}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </>
  );

  if (isMobile) {
    return (
      <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose} accessibilityViewIsModal>
        <View style={ticketChrome.sheetBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <KeyboardAvoidingView behavior="padding" style={styles.keyboardWrap}>
            <View style={[ticketChrome.sheetCard, { maxHeight: winH * 0.96, paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
              <View style={ticketChrome.grabber} />
              <View style={ticketChrome.sheetHeader}>
                <Text style={ticketChrome.sheetSubtitle}>Cuenta corriente</Text>
                <TouchableOpacity
                  onPress={onClose}
                  style={ticketChrome.sheetCloseBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.sheetCloseGlyph}>×</Text>
                </TouchableOpacity>
              </View>
              <ModalAwareScrollView
                style={styles.sheetScroll}
                contentContainerStyle={styles.sheetScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {content}
              </ModalAwareScrollView>
              <View style={ticketChrome.foot}>
                <TouchableOpacity
                  style={[ticketChrome.btnGhost, { flex: 1 }]}
                  onPress={onClose}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Text style={ticketChrome.btnGhostText}>Cerrar</Text>
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose} accessibilityViewIsModal>
      <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
        <View
          style={[
            posUi.modalCard,
            modalShellShared.modalCardShell,
            modalExperience.modalWidthStandard,
            { gap: spacing.sm, width: '100%', alignSelf: 'center', maxHeight: '96%' },
          ]}
        >
          <ModalScreenHeader title="Cuenta corriente" onClose={onClose} />
          <ModalFormScroll contentContainerStyle={modalExperience.formPanel}>{content}</ModalFormScroll>
          <View style={[posUi.modalActions, modalExperience.footerActions]}>
            <TouchableOpacity
              style={[posUi.secondaryButton, posUi.modalButtonHalf]}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Text style={posUi.secondaryButtonText}>Cerrar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  keyboardWrap: {
    width: '100%',
    // Acota el KAV al backdrop: sin esto el maxHeight % del sheet no resuelve en web
    maxHeight: '100%',
    justifyContent: 'flex-end',
  },
  sheetScroll: {
    flexShrink: 1,
    minHeight: 0,
  },
  sheetScrollContent: {
    paddingHorizontal: 20,
    paddingBottom: spacing.lg,
    gap: spacing.md,
  },
  pickerTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  pickerHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  pickerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  chipText: {
    fontSize: 13,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  changeSupplierBtn: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
  },
  changeSupplierText: {
    ...typography.caption,
    color: ticketColors.brandPrimary,
    fontWeight: '700',
  },
  metricsRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  metric: {
    flex: 1,
    minWidth: 120,
  },
  metricLabel: {
    fontSize: 11,
    color: ticketColors.ink3,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  metricWarn: {
    color: ticketColors.warn,
  },
  creditHint: {
    fontSize: 13,
    color: ticketColors.ink2,
    marginTop: spacing.xs,
  },
  section: {
    gap: spacing.xs,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  pendingToggle: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper2,
  },
  pendingToggleActive: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorder,
  },
  pendingToggleText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  pendingToggleTextActive: {
    color: ticketColors.brandInk,
  },
  heroActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  payBalanceBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: ticketColors.brandPrimary,
  },
  payBalanceBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },
  entryRow: {
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  entryRowLast: {
    borderBottomWidth: 0,
  },
  entryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  entryAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
});
